import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ApiError, checkoutApi } from '@/lib/api';
import { useCurrentUser } from '@/lib/auth';
import { Skeleton } from '@/components/ui/skeleton';
import { OwlPostBadge } from '@/components/OwlPostBadge';
import { PriceDisplay } from '@/components/PriceDisplay';
import { Badge } from '@/components/ui/badge';

export function Orders() {
  const navigate = useNavigate();
  const me = useCurrentUser();
  const orders = useQuery({
    queryKey: ['orders'],
    queryFn: async () => (await checkoutApi.orders()).data,
    enabled: !!me.data,
  });

  // A stale `me.data` (session expired server-side after the /me probe
  // populated the query cache, staleTime: 60_000) can still get a 401
  // here. Bounce to login rather than showing the plain "Sign in..."
  // text below: `me.data` is still truthy in the cache, so the Header's
  // user menu keeps showing "Signed in as..." — a text-only empty state
  // here would contradict it with no way to act.
  const sessionExpired = orders.error instanceof ApiError && orders.error.status === 401;
  useEffect(() => {
    if (sessionExpired) navigate('/login?next=/orders', { replace: true });
  }, [sessionExpired, navigate]);

  if (!me.data) {
    return <p className="text-center text-(--color-ink-muted) py-12">Sign in to view your orders.</p>;
  }
  if (orders.isLoading || sessionExpired) return <Skeleton className="h-32" />;

  const list = orders.data ?? [];
  if (list.length === 0) {
    return <p className="text-center text-(--color-ink-muted) py-12">No orders yet.</p>;
  }

  return (
    <div className="space-y-4">
      <h1 className="font-display text-3xl">Your Orders</h1>
      <ul className="space-y-3">
        {list.map((o) => (
          <li key={o.id} className="rounded-lg border bg-card p-4 flex items-center gap-4">
            <Link to={`/orders/${encodeURIComponent(o.id)}`} className="contents">
              <OwlPostBadge orderId={o.id} />
              <div className="flex-1">
                <p className="text-sm text-(--color-ink-muted)">
                  {new Date(o.placed_at).toLocaleString()} · {o.items.length} item{o.items.length === 1 ? '' : 's'}
                </p>
              </div>
              <Badge variant={o.status === 'fulfilled' ? 'gold' : 'secondary'}>{o.status}</Badge>
              <PriceDisplay knuts={o.total_knuts} />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
